import classNames from 'classnames';

import Icon from '../Icon/Icon';
import NoticeBannerContent from './NoticeBannerContent';
import { getBannerAccentColour, getBannerBorderColour, getBannerTintColour } from './Utils/bannerColour';

export const DEFAULT_NOTICE_ICON = 'info';

interface NoticeBannerProps {
	text: string;
	/** Optional short heading rendered above the message, in the banner's accent colour. */
	title?: string | null;
	/**
	 * Optional line above the title naming which leg of the customer's journey the notice applies to.
	 * Only used where that is ambiguous — a return booking whose two legs don't both match.
	 */
	scope?: string | null;
	/** The staff-chosen colour. Drives the tint, the border and the accent — never the body text. */
	colour?: string | null;
	/** A Material Symbols icon name. Falls back to the generic info icon. */
	icon?: string | null;
	/** When provided, a dismiss button is rendered on the right. */
	onDismiss?: () => void;
	className?: string;
}

export default function NoticeBanner({ text, title, scope, colour, icon, onDismiss, className }: NoticeBannerProps) {
	// Inline styles rather than classes: every colour here comes from the colour staff picked on the
	// banner itself, so there is no fixed set of classes to reach for.
	const accent = getBannerAccentColour(colour);

	return (
		<div
			className={classNames('notice-banner', className)}
			style={{
				background: getBannerTintColour(colour),
				borderColor: getBannerBorderColour(colour),
				borderLeftColor: accent,
			}}
		>
			<span className="notice-banner__icon" style={{ color: accent }}>
				<Icon name={icon || DEFAULT_NOTICE_ICON} useMaterialIcons />
			</span>
			<div className="notice-banner__body">
				{scope && <p className="notice-banner__scope">{scope}</p>}
				{title && (
					<p className="notice-banner__title" style={{ color: accent }}>
						{title}
					</p>
				)}
				<div className="notice-banner__text">
					<NoticeBannerContent text={text} />
				</div>
			</div>
			{onDismiss && (
				<button
					type="button"
					className="notice-banner__dismiss"
					onClick={onDismiss}
					aria-label="Dismiss notice"
					data-test-id="dismiss-notice-banner"
				>
					<Icon name="close" useMaterialIcons />
				</button>
			)}
		</div>
	);
}
